import { z } from 'zod';
import { transportModeSchema } from '../enums.js';
import { DOKLESS_ACCEPTED, doklessRejectionWireSchema } from './answer.schema.js';
import { alertSchema, isoDate, isoDateTime, locationSchema } from './tracking-v2.schema.js';
// What Dokless sends Plancia (06-dokless-integration.md §2): the tracking
// request (push body and pull item), backoffice actions, and the shapes of the
// Dokless-hosted endpoints Plancia calls.
export const INTAKE_REFERENCE_TYPES = ['container', 'bl', 'booking', 'awb', 'vehicle'];
export const TRACEABLE_STATUSES = ['traceable', 'manual', 'degraded', 'complete'];
/**
 * An instant with its zone: Dokless's push writes `next_check_at` as PHP's ATOM
 * (`2026-10-16T10:00:00+02:00`, `PlanciaManager::buildTrackingRequest`), `Z` is
 * accepted too; Plancia reads it as the instant it is (ADR 0088).
 */
const instantWithZone = z.iso.datetime({ offset: true });
/** `POST /dokless/v2/trackings` body (upsert on `dokless_id`). */
export const trackingRequestSchema = z.object({
    dokless_id: z.string().min(1).max(64),
    /** `null` when the tracking has no company (Dokless sends the key with `null`). */
    company_id: z.string().max(64).nullable().optional(),
    shipment_ids: z.array(z.string()).optional(),
    transport_mode: transportModeSchema,
    reference: z.string().min(1).max(64),
    reference_type: z.enum(INTAKE_REFERENCE_TYPES),
    containers: z.array(z.string()),
    bills_of_lading: z.array(z.string()),
    bookings: z.array(z.string()),
    carrier: z.object({
        code: z.string().nullable(),
        name: z.string().nullable(),
    }),
    origin: locationSchema,
    destination: locationSchema,
    etd: isoDate.nullable().optional(),
    eta: isoDate.nullable().optional(),
    booked_conveyance: z
        .object({
        name: z.string().nullable(),
        imo: z.string().nullable(),
        voyage: z.string().nullable(),
    })
        .nullable()
        .optional(),
    traceable_status: z.enum(TRACEABLE_STATUSES),
    next_check_at: instantWithZone.nullable().optional(),
    /** `null` when Dokless knows no change date. */
    updated_at: instantWithZone.nullable().optional(),
});
/** `202` answer to an intake. */
export const trackingAcceptedSchema = z.object({
    /** null when automatic mode is off: the tracking became an offer «Da importare» (ADR 0086). */
    shipment_id: z.string().nullable(),
    dokless_id: z.string(),
    status: z.literal('received'),
    revision: z.int().min(0),
});
/** `POST /dokless/v2/trackings/{dokless_id}/actions`. */
export const trackingActionSchema = z.object({
    action: z.enum(['track_now', 'release_to_manual', 'resume']),
    reason: z.string().max(500).optional(),
    user: z.string().max(128).optional(),
});
/** `GET /dokless/v2/ping` and Dokless's `GET /ping`. */
export const pingSchema = z.object({
    ok: z.literal(true),
    provider: z.string(),
    version: z.string(),
    server_time: isoDateTime,
});
/**
 * One item of Dokless's `GET /api/internal/plancia/trackings` (pull). This is
 * the legacy shape Dokless serves today plus the ★ additions of §2.2; Plancia
 * normalises it into a `trackingRequestSchema` at intake.
 */
export const doklessPullItemSchema = z.looseObject({
    id: z.string(),
    company_id: z.string().nullable().optional(),
    shipment_ids: z.array(z.string()).optional(),
    transport_mode: z.string().optional(),
    reference: z.string(),
    reference_type: z.string(),
    containers: z.array(z.string()),
    bills_of_lading: z.array(z.string()),
    bookings: z.array(z.string()),
    /**
     * A SCAC, Dokless's shipping-line code, or still the old ShipsGo name (Dokless, 17/10: kept until it
     * announces the new `/shipping-lines`): any string, or missing.
     */
    carrier_scac: z.string().nullable().optional(),
    /** `{code, name}`; the old shape (a bare ShipsGo name) is read as `{code: null, name}`. */
    shipping_line: z
        .union([
        z.object({ code: z.string().nullable().optional(), name: z.string().nullable().optional() }),
        z.string().transform((name) => ({ code: null, name })),
    ])
        .nullable()
        .optional(),
    /** `null` when no linked shipment has any data (Dokless `PlanciaManager::shipmentFacts`): no facts. */
    shipment: z
        .looseObject({
        origin_port: z.string().nullable(),
        origin_city: z.string().nullable().optional(),
        origin_unlocode: z.string().nullable().optional(),
        destination_port: z.string().nullable(),
        destination_city: z.string().nullable().optional(),
        destination_unlocode: z.string().nullable().optional(),
        etd: z.string().nullable(),
        eta: z.string().nullable(),
        vessel: z.string().nullable().optional(),
        voyage: z.string().nullable().optional(),
        shipping_line: z.string().nullable().optional(),
    })
        .nullable(),
    traceable_status: z.string(),
    next_check_at: z.string().nullable().optional(),
    updated_at: z.string().optional(),
});
export const doklessPullPageSchema = z.object({
    items: z.array(doklessPullItemSchema),
    next_cursor: z.string().nullable().optional(),
});
/** `POST /api/internal/plancia/trackings/{id}/note`. */
export const doklessNoteSchema = z.object({
    level: z.enum(['info', 'warn', 'error']),
    kind: z.string().max(48).optional(),
    message: z.string().max(250),
    handover: z.boolean().optional(),
    resume: z.boolean().optional(),
    problem: alertSchema.nullable().optional(),
});
/**
 * Dokless's `201` answer to a payload, or the stale-revision `200` of 06 §3.5. Dokless's ingest of
 * today (PlanciaManager.php:273–283) answers `tracking_id, events, complete, vessel, eta, next_check_at,
 * problem, exceptions, eta_estimate`: no `revision`, no `alerts` (owner, 17/10: accepted as it is;
 * Plancia keeps the revision it sent on its own side).
 */
export const doklessIngestResponseSchema = z.union([
    z.looseObject({
        tracking_id: z.string(),
        revision: z.int().optional(),
        events: z.int(),
        complete: z.boolean(),
        vessel: z.boolean().optional(),
        eta: z.string().nullable().optional(),
        next_check_at: z.string().nullable(),
        problem: z.boolean().nullable().optional(),
        exceptions: z.int().nullable().optional(),
        eta_estimate: z.boolean().nullable().optional(),
        alerts: z.int().optional(),
        /** Dokless's rules v1 §10.1: what it applied, and each field it discarded (read by `parseDoklessAnswer`). */
        accepted: z.enum(DOKLESS_ACCEPTED).optional(),
        rejected: z.array(doklessRejectionWireSchema).optional(),
    }),
    z.looseObject({ ignored: z.literal('stale_revision') }),
]);
/**
 * `GET /api/internal/plancia/shipping-lines` item: the old list and the new format
 * (`code`, `alternative_code`, `name`, `mode`, `traceable`; Dokless, 17/10) are both read.
 */
export const doklessShippingLineSchema = z.looseObject({
    code: z.string(),
    alternative_code: z.string().nullable().optional(),
    /** Missing or null in the old list: the code stands for it. */
    name: z.string().nullable().optional(),
    mode: z.string().nullable().optional(),
    /** A boolean in the new format; the old list may say 0/1. */
    traceable: z
        .union([z.boolean(), z.literal(0), z.literal(1)])
        .nullable()
        .optional()
        .transform((value) => (value === null || value === undefined ? undefined : Boolean(value))),
    traceable_by: z.string().nullable().optional(),
    days_after_departure: z.int().nullable().optional(),
});
//# sourceMappingURL=intake.schema.js.map